import debounce from 'debounce-promise';

/**
 * Router
 *
 * @package     riot.router
 * @author      Yujiro Takahashi <yujiro@cro-co.co.jp>
 */
class Router {
    /**
     * コンストラクタ
     *
     * @access public
     */
    constructor() {
        this.RE_ORIGIN = /^.+?\/\/+[^/]+/;
        this.POPSTATE = 'popstate';
        this.HASHCHANGE = 'hashchange';
        this.MAX_EMIT_STACK_LEVEL = 3;

        this.win = typeof window != 'undefined' && window;
        this.doc = typeof document != 'undefined' && document;
        this.hist = this.win && history;
        this.loc = this.win && (this.hist.location || this.win.location); // see html5-history-api
        this.emitDebounce = debounce(this.emit.bind(this), 1);
        this.clickEvent = this.doc && this.doc.ontouchstart ? 'touchstart' : 'click';

        this._routeFound = false;
        this._current = null;
        this._emitStack = [];
        this._emitStackLevel = 0;

        this._base = '#';
        this._args = [];
        this._routing = new Map();
        this._parser = this.DEFAULT_PARSER;
        this._secondParser = this.DEFAULT_SECOND_PARSER;

        this._stateActions = new Map();
    }

    /**
     * Default parser. You can replace it via router.parser method.
     * @param {string} path - current path (normalized)
     * @returns {array} array
     */
    DEFAULT_PARSER(path) {
        return path.split(/[/?#]/);
    }

    /**
     * Default parser (second). You can replace it via router.parser method.
     * @param {string} path - current path (normalized)
     * @param {string} filter - filter string (normalized)
     * @returns {array} array
     */
    DEFAULT_SECOND_PARSER(path, filter) {
        let f = filter
            .replace(/\?/g, '\\?')
            .replace(/\*/g, '([^/?#]+?)')
            .replace(/\.\./, '.*');
        let re = new RegExp(("^" + f + "$"));
        let args = path.match(re);
    
        if (args) {
            return args.slice(1);
        }
    }

    /**
     * Set the window listeners to trigger the routes
     * @param {boolean} autoExec - see route.start
     */
    start(autoExec) {
        this.win.addEventListener(this.POPSTATE, this.emitDebounce);
        this.win.addEventListener(this.HASHCHANGE, this.emitDebounce);
        this.doc.addEventListener(this.clickEvent, this.click.bind(this));

        if (autoExec) {
            this.emit(true); 
        }
    }

    stop() {
        if (this.win) {
            this.win.removeEventListener(this.POPSTATE, this.emitDebounce);
            this.win.removeEventListener(this.HASHCHANGE, this.emitDebounce);
            this.doc.removeEventListener(this.clickEvent, this.click.bind(this));
        }

        this.stopper();
    }

    /**
     * Compare paths
     * @param {string} path
     * @returns {boolean}
     */
    compare(path) {
        let current = this.getPathFromBase();
        return (current === path);
    }

    on(key, action) {
        /* Overwrite duplicate key */
        this._stateActions.set(key, action);
//        this._emitAction();
    }

    getArgs() {
        let path = this.getPathFromBase();

        for (let filter of this._routing.keys()) {
            let args = this._secondParser(
                this.normalize(path), 
                this.normalize(filter)                
            );
            if (typeof args !== 'undefined') {
                return args;
            }
        }
        return false;
    }

    /**
     * Get the part after domain name
     * @param {string} href - fullpath
     * @returns {string} path from root
     */
    getPathFromRoot(href) {
        return (href || this.loc.href).replace(this.RE_ORIGIN, '');
    }
    
    /**
     * Get the part after base
     * @param {string} href - fullpath
     * @returns {string} path from base
     */
    getPathFromBase(href) {
        let base = this._base;
        return base[0] === '#'
            ? (href || this.loc.href || '').split(base)[1] || ''
            : (this.loc ? this.getPathFromRoot(href) : href || '').replace(base, '');
    }

    normalize(path) {
        return path.replace(/^\/|\/$/, '')
    }

    emit(force) {
        // the stack is needed for redirections
        let isRoot = this._emitStackLevel === 0;
        if (this.MAX_EMIT_STACK_LEVEL <= this._emitStackLevel) {
            return ;
        }
      
        this._emitStackLevel++;
        this._emitStack.push(() => {
            let path = this.getPathFromBase();
            if (force || path !== this._current) {
                this.emitter(path);
                this._current = path;
            }
        });
      
        if (isRoot) {
            let first;
            while (first = this._emitStack.shift()) { 
                first();
            } // stack increses within this call
            this._emitStackLevel = 0;
        }
    }
      
    click(e) {
        if (
            e.which !== 1 // not left click
            || e.metaKey || e.ctrlKey || e.shiftKey // or meta keys
            || e.defaultPrevented // or default prevented
        ) { return }
      
        let el = e.target;
        while (el && el.nodeName !== 'A') {
            el = el.parentNode;
        }

        if (
            !el || el.nodeName !== 'A' // not A tag
            || el.hasAttribute('download') // has download attr
            || !el.hasAttribute('href') // has no href attr
            || el.target && el.target !== '_self' // another window or frame
            || el.href.indexOf(this.loc.href.match(this.RE_ORIGIN)[0]) === -1 // cross origin
        ) { return }
      
        let base = this._base;
      
        if (el.href !== this.loc.href
            && (
                el.href.split('#')[0] === this.loc.href.split('#')[0] // internal jump
                || base[0] !== '#' && this.getPathFromRoot(el.href).indexOf(base) !== 0 // outside of base
                || base[0] === '#' && el.href.split(base)[0] !== this.loc.href.split(base)[0] // outside of #base
                || !this.go(this.getPathFromBase(el.href), el.title || this.doc.title) // route not found
        )) { return }

        e.preventDefault();
    }

    /**
     * Go to the path
     * @param {string} path - destination path
     * @param {string} title - page title
     * @param {boolean} shouldReplace - use replaceState or pushState
     * @returns {boolean} - route not found flag
     */
    go(path, title, shouldReplace) {
        // Server-side usage: directly execute handlers for the path
        if (!this.hist) {
            return this.emitter(this.getPathFromBase(path));
        }
    
        path = this._base + this.normalize(path);
        title = title || this.doc.title;
        // browsers ignores the second parameter `title`
        shouldReplace
            ? this.hist.replaceState(null, title, path)
            : this.hist.pushState(null, title, path);
        // so we need to set it manually
        this.doc.title = title;
        this._routeFound = false;
        this.emit();
        return this._routeFound;
    }

    /**
     * Helper function to get url query as an object
     * @returns {object} parsed query
     */
    query() {
        let q = {};
        let href = this.loc.href || this._current;
        href.replace(/[?&](.+?)=([^&]*)/g, (_, k, v) => { q[k] = v; });
        return q
    }


    isString(str) {
        return typeof str == 'string'
    }
      
    /**
     * Go to path or set action
     * a single string:                go there
     * two strings:                    go there with setting a title
     * two strings and boolean:        replace history with setting a title
     * a single function:              set an action on the default route
     * a string/RegExp and a function: set an action on the route
     * @param {(string|function)} first - path / action / filter
     * @param {(string|RegExp|function)} second - title / action
     * @param {boolean} third - replace flag
     */
    mount(first, second, third) {
        if (this.isString(first) && (!second || this.isString(second))) {
            this.go(first, second, third || false); 
        } else if (second) {
            this.register(first, second); 
        } else {
            this.register('@', first);
        }
    }
    
    /**
     * Emitter
     * @param {string} path - path
     */
    emitter(path) {
        this._routing.forEach((collback, filter)=> {
            this._args = this._secondParser(
                this.normalize(path), 
                this.normalize(filter)                
            );
            if (typeof this._args != 'undefined') {
                collback.apply(null, this._args);
                this._routeFound = true;
                this._emitAction();

                return this._routeFound; // exit from loop
            }
        });

        if (!this._routeFound && this._routing.has('@')) {
            let callback = this._routing.get('@');
            callback.call();
        }
    }
  
    /**
     * Register route
     * @param {string} filter - filter for matching to url
     * @param {function} action - action to register
     */
    register(filter, action) {
        if (filter !== '@') {
            filter = '/' + this.normalize(filter);
        }

        this._routing.set(filter, action);
    }


    /**
     * Set the base of url
     * @param {(str|RegExp)} arg - a new base or '#' or '#!'
     */
    base(arg) {
        this._base = arg || '#';
        this._current = this.getPathFromBase(); // recalculate current path
    }
    
    /** Exec routing right now **/
    exec() {
        this.emit(true);
    }
    
    /**
     * Replace the default router to yours
     * @param {function} fn - your parser function
     * @param {function} fn2 - your secondParser function
     */
    parser(fn, fn2) {
        if (!fn && !fn2) {
            // reset parser for testing...
            this._parser = this.DEFAULT_PARSER;
            this._secondParser = this.DEFAULT_SECOND_PARSER;
        }
        if (fn) { 
            this._parser = fn;
        }
        if (fn2) {
            this._secondParser = fn2;
        }
    }

    /**
     * Stop routing
     */
    stopper() {
        if (this.started) {
            this.routing.clear();
            this.started = false;
        }
    }

    _emitAction() {
        if (this._routeFound && (typeof this._args[0] != 'undefined')) {
            if (this._stateActions.has(this._args[0])) {
                let action = this._stateActions.get(this._args[0]);
                action.apply(null, this._args);
//                Promise.resolve(action.apply(null, this._args));
            }
        } // if (this._routeFound && (typeof this._args[0] != 'undefined'))
    }
} // class Router

export default Router;
